import React, { ChangeEvent } from "react";

import {
  Button,
  Checkbox,
  DocumentList,
  InfoPanel,
  TextField,
  Typography,
  CardList,
  Label,
} from "@king-ict/eupisi-ui/components";
import { SelectEvent } from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import BenefitDataFetchStatusEnum from "../../../models/Benefit/BenefitDataFetchStatusEnum";
import BenefitIntegrationPersonData from "../../../models/Benefit/BenefitIntegrationPersonData";
import CriterionProvingTypeEnum from "../../../models/Criterion/CriterionProvingTypeEnum";
import RequestBenefit from "../../../models/Request/RequestBenefit";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import CodeBook from "../../../models/Shared/CodeBook";
import { getPersonsOibs } from "../../../slices/requestSlice";
import DocumentsModal from "../../Request/DocumentsModal/DocumentsModal";
import { useBenefitDocuments } from "../../RequestInfo/Benefits/useBenefitDocuments";
import BenefitDetailsItem from "./BenefitDetailsItem";
import styles from "./BenefitItem.module.scss";

interface Props {
  relationshipsData: CodeBook[];
  consentAccepted: boolean;
  selected: boolean;
  benefit: RequestBenefit;
  benefitTypeId?: number;
  benefitDocuments: RequestDocumentData[];
  onSelect: (benefit: RequestBenefit, isSelected: boolean) => void;
  onDocumentsChange: (
    benefit: RequestBenefit,
    documents: RequestDocumentData[]
  ) => void;
  onRemoveDocumentFromList: (
    benefit: RequestBenefit,
    document: RequestDocumentData
  ) => void;
  handleAddMember: (institutionBenefitId: number) => void;
  handleRemoveMember: (id: number, institutionBenefitId: number) => void;
  handleDetailsChange: (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    institutionBenefitId: number,
    detailsId: number
  ) => void;
  setDocumentsModalOpenId: Function;
  documentsModalOpenId?: number;
  onCloseDocumentModal: Function;
  allDocuments: RequestDocumentData[];
  handleGetBenefitDetailsIntegrationData: (
    data: BenefitIntegrationPersonData,
    institutionBenefitId: number,
    detailsId: number
  ) => void;
  isSent: boolean;
  requestId: number;
}

export default function BenefitItem({
  relationshipsData,
  consentAccepted,
  selected,
  benefit,
  benefitTypeId,
  benefitDocuments,
  onSelect,
  onDocumentsChange,
  handleAddMember,
  handleRemoveMember,
  handleDetailsChange,
  setDocumentsModalOpenId,
  documentsModalOpenId,
  onCloseDocumentModal,
  onRemoveDocumentFromList,
  allDocuments,
  handleGetBenefitDetailsIntegrationData,
  isSent,
  requestId,
}: Props) {
  const { t } = useTranslation();
  const personsOibs = useSelector(getPersonsOibs);

  const handleSelectChange = (checked: boolean) => {
    onSelect(benefit, checked);
    if (!checked && benefitDocuments && benefitDocuments.length > 0) {
      handleRemoveDocuments(benefitDocuments);
    }
  };

  const {
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleConfirmDocumentsModal,
    handleCloseDocumentsModal,
    handleOpenDocumentsModal,
    handleDownloadDocument,
    handleRemoveDocumentFromList,
    handleRemoveDocument,
    handleRemoveDocuments,
  } = useBenefitDocuments({
    benefit,
    requestId,
    onRemoveDocumentFromList: onRemoveDocumentFromList,
    onDocumentsChange: onDocumentsChange,
    setDocumentsModalOpenId: setDocumentsModalOpenId,
    onCloseDocumentModal: onCloseDocumentModal,
  });

  const isIntegrationInfoVisible =
    consentAccepted &&
    benefit.provingTypeId &&
    (benefit.provingTypeId & CriterionProvingTypeEnum.Integration) ===
      CriterionProvingTypeEnum.Integration;

  const isDocumentsVisible =
    benefit.provingTypeId &&
    (benefit.provingTypeId & CriterionProvingTypeEnum.DocumentUpload) ===
      CriterionProvingTypeEnum.DocumentUpload;

  const getStatusColor = (status: BenefitDataFetchStatusEnum) => {
    switch (status) {
      case BenefitDataFetchStatusEnum.Success:
        return "success";
      case BenefitDataFetchStatusEnum.Fail:
        return "danger";
      case BenefitDataFetchStatusEnum.Unknown:
        return undefined;
      default:
        return undefined;
    }
  };

  return (
    <Row>
      <Col xs={12}>
        <Checkbox
          label={benefit.benefitName}
          checked={selected}
          onChange={(e) => handleSelectChange(e.target.checked)}
          disabled={isSent}
        />
      </Col>
      {selected && (
        <Col xs={12}>
          <Row className={`${styles.gutter} ${styles.bottomSpacing}`}>
            {isIntegrationInfoVisible && benefitTypeId === undefined && (
              <Col md={12}>
                <Row>
                  <Col md={12}>
                    <Typography variant="smallHeading">
                      {t("benefit.integrationHeader", {
                        integrationName: benefit.integrationName,
                      })}
                    </Typography>
                    <InfoPanel
                      message={t("benefit.integrationInfoMessage", {
                        documentDescription: benefit.provingDocumentDescription,
                      })}
                      color="success"
                    />
                  </Col>
                </Row>
                {benefit.fetchedIntegrationData && benefit.integrationData ? (
                  <>
                    <Row className={styles.integrationStatusRow}>
                      <Col xs={6}>
                        <TextField
                          plaintext
                          bold
                          label={t("benefit.applicantParentIntegration")}
                          value={personsOibs.applicantParentOib}
                        />
                      </Col>
                      <Col xs={6} className={styles.status}>
                        <Label
                          color={getStatusColor(
                            benefit.integrationData.applicantParentData
                              .fetchStatus
                          )}
                        >
                          {benefit.integrationData.applicantParentData
                            .fetchStatus === BenefitDataFetchStatusEnum.Success
                            ? t("benefit.successIntegration")
                            : t("benefit.failIntegration")}
                        </Label>
                      </Col>
                    </Row>
                    {benefit.integrationData.applicantParentData.fetchStatus ===
                      BenefitDataFetchStatusEnum.Success && (
                      <Row>
                        <Col md={12}>
                          <Typography>
                            {benefit.integrationData.applicantParentData.data}
                          </Typography>
                        </Col>
                      </Row>
                    )}
                    {personsOibs.otherParentOib != null && (
                      <>
                        <Row
                          className={`${styles.integrationStatusRow} ${styles.integrationOtherParentTitle}`}
                        >
                          <Col xs={6}>
                            <TextField
                              plaintext
                              bold
                              label={t("benefit.otherParentIntegration")}
                              value={personsOibs.otherParentOib}
                            />
                          </Col>
                          <Col xs={6} className={styles.status}>
                            <Label
                              color={getStatusColor(
                                benefit.integrationData.otherParentData
                                  .fetchStatus
                              )}
                            >
                              {benefit.integrationData.otherParentData
                                .fetchStatus ===
                              BenefitDataFetchStatusEnum.Success
                                ? t("benefit.successIntegration")
                                : t("benefit.failIntegration")}
                            </Label>
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Col>
            )}
            {isDocumentsVisible && benefitTypeId === undefined && (
              <Col
                className={styles.rowSpace}
                md={isIntegrationInfoVisible ? 6 : 12}
              >
                <Row className={styles.rowSpace}>
                  <Col md={12} className={styles.documents}>
                    <Typography variant="smallHeading">
                      {t("benefit.documentHeader")}
                    </Typography>
                    <InfoPanel
                      message={t("benefit.documentInfoMessage", {
                        documentDescription: benefit.provingDocumentDescription,
                      })}
                      color="warning"
                    />
                  </Col>
                  {!isSent && (
                    <Col md={12}>
                      <Button
                        color="primary"
                        disabled={benefitDocuments.length >= 10}
                        outline
                        icon={<UploadIcon />}
                        onClick={handleOpenDocumentsModal}
                      >
                        {t("documentUpload.additionalDocumentationAttach")}
                      </Button>
                      <DocumentsModal
                        open={
                          documentsModalOpenId === benefit.institutionBenefitId
                        }
                        allDocuments={allDocuments}
                        onClose={handleCloseDocumentsModal}
                        onConfirm={handleConfirmDocumentsModal}
                        onUpload={handleUploadDocuments}
                        onRemove={handleRemoveDocument}
                        onDownload={handleDownloadDocument}
                        onExistingDocumentClick={handleUploadExistingDocument}
                      />
                    </Col>
                  )}
                </Row>
                <Row className={styles.rowSpace}>
                  <Col md={12}>
                    <Typography variant="smallHeading">
                      {t("documentUpload.additionalDocumentationList")}
                    </Typography>
                  </Col>
                  <Col md={12}>
                    <DocumentList
                      documents={benefitDocuments}
                      onRemoveClick={handleRemoveDocumentFromList}
                      onDocumentClick={handleDownloadDocument}
                      removable={!isSent}
                      fullWidth
                    />
                  </Col>
                </Row>
              </Col>
            )}
            {benefitTypeId && (
              <Col className={styles.rowSpace} md={12}>
                <Row className={styles.rowSpace}>
                  <CardList isNumbered={true} isDraggable={false}>
                    {benefit.details &&
                      benefit.details.map((d, i) => (
                        <BenefitDetailsItem
                          key={`${d.id}_ ${benefit}_fragment`}
                          handleRemoveMember={handleRemoveMember}
                          handleDetailsChange={handleDetailsChange}
                          relationshipsData={relationshipsData}
                          benefitDetails={d}
                          benefitDetailsLength={benefit.details.length}
                          integrationName={benefit.integrationName}
                          institutionBenefitId={benefit.institutionBenefitId}
                          handleGetBenefitDetailsIntegrationData={
                            handleGetBenefitDetailsIntegrationData
                          }
                          isSent={isSent}
                        />
                      ))}
                  </CardList>
                </Row>
              </Col>
            )}
            {benefitTypeId && !isSent && (
              <Col
                className={`${styles.rowSpace} ${styles.buttonLayout}`}
                md={12}
              >
                <Button
                  outline
                  icon={<AddIcon />}
                  className={styles.iconButton}
                  onClick={() => handleAddMember(benefit.institutionBenefitId)}
                >
                  {t("benefit.addNewMember")}
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      )}
      <Col xs={12} className={styles.bottomSpacing}>
        <hr />
      </Col>
    </Row>
  );
}
